/**
 *
 * WCCMultiBUOfferPriceAndPaymentInfo
 *
 */

import React from "react";
import {
  showCurrencyAsCode,
  offerPurchaseMethods,
  getCurrencyFractionDigitsOptions,
} from "root/libs/constants/src";
import { IntlPropType, GetCountryCode } from "root/libs/core-libs/src";
import PropTypes from "prop-types";
import { TYPES_OF_OFFERS } from "one-time-shipment/src/containers/GetQuote/constants";
import { DhlText } from "@dhl-official/react-library";
import { Sizes } from "@dhl-official/stencil-library";
import OfferPaymentIcons from "one-time-shipment/src/components/Offers/OfferPaymentIcons";
import WCCOfferCardCTA from "one-time-shipment/src/components/Offers/WCCOfferCardCTA";
import {
  CardPriceWrapper,
  CardPriceHeadline,
  DisclaimerStar,
  StyledVatInfoMessage,
  PaymentInfoAndVatMessageWrapper,
  ConversionFactorText,
  PriceTagWrapper,
} from "./styled";
import PriceLabelTag from "../AccountPricingCard/PriceLabelTag";
import NormalPriceRow from "../AccountPricingCard/NormalPriceRow";

const COPY_ID_PREFIX = "OfferPriceAndPaymentInfo";
export default class WCCMultiBUOfferPriceAndPaymentInfo extends React.PureComponent {
  static propTypes = {
    priceBreakdownAnchor: PropTypes.func,
    priceBreakdownRef: PropTypes.string,
    /** An object that contains all the information required of an offer */
    offer: PropTypes.shape({
      businessUnit: PropTypes.string,
      /** id of the offer */
      key: PropTypes.string,
      id: PropTypes.string,
      /** An array that contains the accepted payment methods */
      acceptedPaymentMethods: PropTypes.arrayOf(PropTypes.string),
      /** Link for Book now button */
      pathToPurchaseLink: PropTypes.string,
      /** Price of the offer */
      price: PropTypes.shape({
        /** A number that contains the price
         */
        amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        /** A valid currency code */
        currencyCode: PropTypes.string,
        tax: PropTypes.string,
        /** Conversion factor for currency */
        currencyConversionFactor: PropTypes.number,
      }),
      euroPrice: PropTypes.shape({
        /** A number that contains the price
         */
        amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        /** A valid currency code */
        currencyCode: PropTypes.string,
        tax: PropTypes.string,
        /** Conversion factor for currency */
        currencyConversionFactor: PropTypes.number,
        /** Source currency for the conversion */
        convertedFromCurrency: PropTypes.string,
      }),
      /** If set will trigger the display of a special scenario card in DGF */
      specialOffers: PropTypes.string,
      priceWithoutDiscount: PropTypes.shape({
        amount: PropTypes.number,
        currencyCode: PropTypes.string,
      }),
    }).isRequired,
    /** Internationalization library */
    intl: IntlPropType.isRequired,
    isBusiness: PropTypes.bool,
    showCurrencyIsoCodeForPrices: PropTypes.bool,
    nativeLanguageCode: PropTypes.string,
    formatCurrencyWithNativeLocale: PropTypes.bool,
    offerIndex: PropTypes.string,
    displayOffer: PropTypes.object,
    /** The purchase method */
    purchaseMethod: PropTypes.oneOf(Object.values(offerPurchaseMethods))
      .isRequired,
    utfDataObject: PropTypes.object,
    toggleFreightP2PModal: PropTypes.func,
  };

  static defaultProps = {
    isBusiness: false,
    showCurrencyIsoCodeForPrices: false,
    nativeLanguageCode: "",
    formatCurrencyWithNativeLocale: false,
    toggleFreightP2PModal: () => {},
  };

  getOfferPrice = () => {
    const { offer } = this.props;

    if (!("price" in offer)) {
      return null;
    }

    if (typeof offer.price.amount !== "undefined" && offer.price.amount > 0) {
      return offer.price.amount;
    }
    return offer.price.amount;
  };

  getEuroPrice = () => {
    const { offer } = this.props;
    return offer?.euroPrice?.amount > 0
      ? offer.euroPrice.amount
      : offer?.euroPrice?.amount || null;
  };

  getOfferCurrency = () => {
    const { offer } = this.props;

    if (!("price" in offer)) {
      return null;
    }

    if (typeof offer.price.currencyCode !== "undefined") {
      return offer.price.currencyCode;
    }
    return null;
  };

  getEuroCurrency = () => {
    const { offer } = this.props;
    return offer?.euroPrice?.currencyCode || null;
  };

  getConversionFactor = () => {
    const { offer } = this.props;
    const conversionFactor = offer?.price?.currencyConversionFactor;
    return conversionFactor && !Number.isNaN(Number(conversionFactor))
      ? Number(conversionFactor)
      : null;
  };

  getPriceLabel = () => {
    const { offer, isBusiness } = this.props;

    if (!isBusiness && "price" in offer) {
      switch (offer.price.tax) {
        case "WITH":
          return this.getCopy("priceWithVat");
        case "OPTIONAL":
          return this.getCopy("priceOptionalVat");
        default:
          return this.getCopy("priceWithoutVat");
      }
    }

    return this.getCopy("priceFrom");
  };

  getCopy = (id, values, prefix = COPY_ID_PREFIX) => {
    const { intl } = this.props;
    return intl.formatMessage({ id: `${prefix}.${id}` }, values);
  };

  render() {
    const {
      priceBreakdownAnchor,
      priceBreakdownRef,
      intl,
      offer,
      isBusiness,
      showCurrencyIsoCodeForPrices,
      nativeLanguageCode,
      formatCurrencyWithNativeLocale,
      offerIndex,
      purchaseMethod,
      displayOffer,
      utfDataObject,
      toggleFreightP2PModal,
    } = this.props;

    const offerPrice = this.getOfferPrice();
    const euroPrice = this.getEuroPrice();
    const currencyCode = this.getOfferCurrency();
    const euroCurrencyCode = this.getEuroCurrency();
    const priceLabel = this.getPriceLabel();
    const countryCode = GetCountryCode(intl.locale);
    const priceFormatLocale = formatCurrencyWithNativeLocale
      ? `${nativeLanguageCode}-${countryCode}`
      : intl.locale;
    const isCurrencyCodeAvailable =
      currencyCode !== "" && currencyCode !== null;

    // TODO - Evaluate if this is needed with Analytics or remove it
    // const shippingModesString = getUtfAddOnString(displayOffer.addOns);
    const isInvoiceOnly =
      offer.acceptedPaymentMethods?.length === 1 &&
      offer.acceptedPaymentMethods[0] === "INVOICE";
    const isPaymentIcons = offer.acceptedPaymentMethods;

    return (
      <React.Fragment>
        {!(offerPrice === null || typeof offerPrice === "undefined") ? (
          <React.Fragment>
            <span
              id={`${offer.key}-starBehindPrice`}
              className="visually-hidden"
            >
              {". "}
              {this.getCopy("screenReaderLabel_starBehindPrice")}
            </span>
            <CardPriceWrapper
              id={`${offer.key}-price`}
              data-testid={`wcc-offer-card-price-wrapper-${offerIndex}`}
              className={euroPrice ? "has-euro-price" : ""}
            >
              <DhlText className="price-label" size={Sizes.XS} isParagraph>
                {priceLabel}
              </DhlText>
              <span className="visually-hidden">,</span>
              <PriceTagWrapper>
                {offer?.priceWithoutDiscount?.amount && isBusiness && (
                  <PriceLabelTag
                    intl={intl}
                    priceWithoutDiscount={offer?.priceWithoutDiscount?.amount}
                    price={offer?.price?.amount}
                    variant="red"
                  />
                )}
                <CardPriceHeadline
                  dataTestid="offer-price"
                  data-testvalue={offerPrice}
                  priority={4}
                  weight="700"
                  bottomMargin="7px"
                >
                  {showCurrencyIsoCodeForPrices &&
                    isCurrencyCodeAvailable &&
                    `${currencyCode} ${intl.formatNumber(
                      offerPrice,
                      getCurrencyFractionDigitsOptions(currencyCode)
                    )}`}
                  {!showCurrencyIsoCodeForPrices &&
                    isCurrencyCodeAvailable &&
                    new Intl.NumberFormat(priceFormatLocale, {
                      style: "currency",
                      currency: currencyCode,
                      currencyDisplay: showCurrencyAsCode.includes(currencyCode)
                        ? "code"
                        : "symbol",
                    }).format(offerPrice)}
                  {currencyCode === "" &&
                    intl.formatNumber(offerPrice, {
                      minimumFractionDigits: 2,
                    })}
                  <DisclaimerStar aria-hidden="true">&nbsp;*</DisclaimerStar>
                </CardPriceHeadline>
                {euroPrice && (
                  <React.Fragment>
                    <CardPriceHeadline
                      dataTestid="offer-euro-price"
                      data-testvalue={euroPrice}
                      priority={4}
                      weight="700"
                      bottomMargin="7px"
                    >
                      {showCurrencyIsoCodeForPrices &&
                        euroCurrencyCode &&
                        `${euroCurrencyCode} ${intl.formatNumber(
                          euroPrice,
                          getCurrencyFractionDigitsOptions(euroCurrencyCode)
                        )}`}
                      {!showCurrencyIsoCodeForPrices &&
                        euroCurrencyCode &&
                        new Intl.NumberFormat(priceFormatLocale, {
                          style: "currency",
                          currency: euroCurrencyCode,
                          currencyDisplay: showCurrencyAsCode.includes(
                            euroCurrencyCode
                          )
                            ? "code"
                            : "symbol",
                        }).format(euroPrice)}
                      {euroCurrencyCode === "" &&
                        intl.formatNumber(euroPrice, {
                          minimumFractionDigits: 2,
                        })}
                      <DisclaimerStar aria-hidden="true">
                        &nbsp;*
                      </DisclaimerStar>
                    </CardPriceHeadline>
                    {this.getConversionFactor() && (
                      <ConversionFactorText
                        dataTestid="conversion-factor"
                        size={Sizes.XS}
                        isParagraph
                      >
                        {`(1 ${euroCurrencyCode} = ${Number(
                          this.getConversionFactor()
                        ).toFixed(5)} ${currencyCode})`}
                      </ConversionFactorText>
                    )}
                  </React.Fragment>
                )}
              </PriceTagWrapper>
            </CardPriceWrapper>
            <NormalPriceRow
              intl={intl}
              offer={offer}
              showCurrencyIsoCodeForPrices={showCurrencyIsoCodeForPrices}
            />
            <WCCOfferCardCTA
              variationType="WCCMultiBUOfferCardCTA"
              offerIndex={offerIndex}
              intl={intl}
              displayOffer={displayOffer}
              purchaseMethod={purchaseMethod}
              utfDataObject={utfDataObject}
              toggleFreightP2PModal={toggleFreightP2PModal}
            />
            {isPaymentIcons && (
              <PaymentInfoAndVatMessageWrapper
                isPaymentIcons={isPaymentIcons}
                isInvoiceOnly={isInvoiceOnly}
              >
                <OfferPaymentIcons
                  intl={intl}
                  offer={offer}
                  iconVariation={TYPES_OF_OFFERS.WCC}
                  dataTestid={`wcc-offer-card-payment-methods-${offerIndex}`}
                  isBusiness={isBusiness}
                />
                {isBusiness && offer.price.tax === "WITHOUT" && (
                  <StyledVatInfoMessage
                    priceBreakdownAnchor={priceBreakdownAnchor}
                    priceBreakdownRef={priceBreakdownRef}
                    offerKey={offer.key}
                    vatInfoVariation={TYPES_OF_OFFERS.WCC}
                  />
                )}
              </PaymentInfoAndVatMessageWrapper>
            )}
          </React.Fragment>
        ) : (
          <WCCOfferCardCTA
            variationType="WCCMultiBUOfferCardCTA"
            offerIndex={offerIndex}
            intl={intl}
            displayOffer={displayOffer}
            purchaseMethod={purchaseMethod}
            utfDataObject={utfDataObject}
            toggleFreightP2PModal={toggleFreightP2PModal}
          />
        )}
      </React.Fragment>
    );
  }
}
